import type {
  Alerta,
  AlertasRespuesta,
  AmbitoRegla,
  CalcularRutaBody,
  CalculoRuta,
  CatalogosRespuesta,
  EstadoAlerta,
  EstadoDiarioRespuesta,
  EstadoUnidad,
  EstadoViaje,
  FechaDia,
  FiltroAlertas,
  FiltroTroncales,
  FlotaEstadoRespuesta,
  JornadasRespuesta,
  LoginBody,
  LoginRespuesta,
  MensajeStream,
  MicrotrackEstado,
  NombresRuteoRespuesta,
  RecorridoRespuesta,
  ReglaAlerta,
  ReglaAlertaActualizacionBody,
  ReglaAlertaBody,
  Ruta,
  RutaBody,
  RutaGuardada,
  TipoAlerta,
  TroncalDetalle,
  TroncalesRespuesta,
  ViajeDetalle,
  ViajesRespuesta,
} from '@tms/contratos';

export type { AlertasRespuesta };

export interface FiltrosFlota {
  empresa?: string;
  unidadNegocio?: string;
  estado?: EstadoUnidad;
}

/** Query de GET /viajes (ver torre.ts: estado, empresa, unidadNegocio, troncalId, sinRuta, desde, hasta, q). */
export interface FiltrosViajes {
  estado?: EstadoViaje;
  empresa?: string;
  unidadNegocio?: string;
  troncalId?: number;
  sinRuta?: boolean;
  desde?: string;
  hasta?: string;
  q?: string;
  pagina?: number;
  tamanio?: number;
}

/** Query de GET /configuracion/reglas-alerta. */
export interface FiltrosReglas {
  tipo?: TipoAlerta;
  ambito?: AmbitoRegla;
}

export type { FiltroAlertas, FiltroTroncales };

export type EstadoConexion = 'conectado' | 'reconectando';

/** Interfaz común de la capa de datos: la implementan ApiReal y ApiMock. */
export interface ApiTms {
  /** true si los datos son ficticios (modo demostración). */
  readonly esMock: boolean;
  login(body: LoginBody): Promise<LoginRespuesta>;
  flotaEstado(filtros?: FiltrosFlota): Promise<FlotaEstadoRespuesta>;
  viajes(filtros?: FiltrosViajes): Promise<ViajesRespuesta>;
  viaje(id: number): Promise<ViajeDetalle>;
  recorrido(id: number, muestreoSeg?: number): Promise<RecorridoRespuesta>;
  /** PUT /viajes/:id/ruta (solo PLANIFICADO; ruta activa de su troncal). */
  cambiarRutaViaje(viajeId: number, rutaId: number): Promise<ViajeDetalle>;
  alertas(filtros?: FiltroAlertas): Promise<AlertasRespuesta>;
  resolverAlerta(id: number, motivo?: string): Promise<Alerta>;
  justificarAlerta(id: number, motivo: string): Promise<Alerta>;
  jornadas(fecha: FechaDia): Promise<JornadasRespuesta>;
  estadoDiario(fecha: FechaDia): Promise<EstadoDiarioRespuesta>;
  /** Rango de días (máx. 31), una sola llamada. */
  estadoDiarioRango(desde: FechaDia, hasta: FechaDia): Promise<EstadoDiarioRespuesta>;
  catalogos(): Promise<CatalogosRespuesta>;
  microtrackEstado(): Promise<MicrotrackEstado>;

  // ---------- Ruteo (ruteo.ts) ----------
  troncales(filtro?: FiltroTroncales): Promise<TroncalesRespuesta>;
  troncal(id: number): Promise<TroncalDetalle>;
  /** Devuelve la ruta con `avisos` (no bloqueantes) y `viajesAsignados` (D-47). */
  crearRuta(troncalId: number, body: RutaBody): Promise<RutaGuardada>;
  actualizarRuta(id: number, body: RutaBody): Promise<RutaGuardada>;
  /** Baja lógica (activa = false). */
  bajaRuta(id: number): Promise<Ruta>;
  calcularRuta(body: CalcularRutaBody): Promise<CalculoRuta>;
  /** GET /ruteo/nombres: nombres de rutas y paradas por id (máx. NOMBRES_RUTEO_MAX_IDS ids en total por llamada). */
  nombresRuteo(rutas: number[], paradas: number[]): Promise<NombresRuteoRespuesta>;

  // ---------- Reglas de alerta (reglas-alerta.ts) ----------
  reglasAlerta(filtros?: FiltrosReglas): Promise<ReglaAlerta[]>;
  crearReglaAlerta(body: ReglaAlertaBody): Promise<ReglaAlerta>;
  actualizarReglaAlerta(id: number, body: ReglaAlertaActualizacionBody): Promise<ReglaAlerta>;
  borrarReglaAlerta(id: number): Promise<void>;

  /**
   * Abre el stream en vivo. Devuelve la función para cerrarlo.
   * `onEstado` informa si la conexión está viva o reconectando.
   */
  suscribir(onMensaje: (m: MensajeStream) => void, onEstado?: (e: EstadoConexion) => void): () => void;
}

/** Error de la API con mensaje ya en español, apto para mostrar. */
export class ErrorApiTms extends Error {
  readonly status: number;
  constructor(status: number, mensaje: string) {
    super(mensaje);
    this.name = 'ErrorApiTms';
    this.status = status;
  }
}

/** Mensaje cuando el motor de ruteo (OSRM) no responde: la API devuelve 503. */
export const MENSAJE_MOTOR_CAIDO =
  'El motor de ruteo no está disponible en este momento. Los puntos que marcaste no se perdieron: probá calcular de nuevo en unos minutos.';

export type { EstadoAlerta };
